import { useState, useEffect, useRef } from 'react';
import { Bell, BellOff, Volume2, ChevronDown } from 'lucide-react';
import { requestNotificationPermission, onForegroundMessage } from '../lib/firebase';
import {
  isNativeNotifierAvailable,
  hasNativeNotificationPermission,
  requestNativeNotificationPermission,
  nativeNotify,
  nativeCancel,
  getNativeFcmToken,
  refreshNativeFcmToken,
} from '../lib/nativeNotifier';

// Generate notification sounds using Web Audio API
const createSound = (type, audioCtx) => {
  const oscillator = audioCtx.createOscillator();
  const gainNode = audioCtx.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(audioCtx.destination);

  switch (type) {
    case 'campana': {
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(830, audioCtx.currentTime);
      oscillator.frequency.setValueAtTime(1100, audioCtx.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(830, audioCtx.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.4, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.6);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.6);
      break;
    }
    case 'alerta': {
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
      oscillator.frequency.setValueAtTime(660, audioCtx.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime + 0.3);
      oscillator.frequency.setValueAtTime(660, audioCtx.currentTime + 0.45);
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.7);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.7);
      break;
    }
    case 'suave': {
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(523, audioCtx.currentTime);
      oscillator.frequency.setValueAtTime(659, audioCtx.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(784, audioCtx.currentTime + 0.4);
      gainNode.gain.setValueAtTime(0.25, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.8);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.8);
      break;
    }
    case 'moderna': {
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(587, audioCtx.currentTime);
      oscillator.frequency.setValueAtTime(784, audioCtx.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(988, audioCtx.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.35, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.5);
      break;
    }
    default:
      break;
  }
};

const SOUNDS = [
  { id: 'campana', label: '🔔 Campana' },
  { id: 'alerta', label: '🚨 Alerta' },
  { id: 'suave', label: '📱 Suave' },
  { id: 'moderna', label: '🎵 Moderna' },
];

export default function BalanceNotification() {
  const [pendingCount, setPendingCount] = useState(0);
  const [enabled, setEnabled] = useState(true); // Will be synced from server
  const [selectedSound, setSelectedSound] = useState(() => {
    return localStorage.getItem('notif_sound') || 'campana';
  });
  const [showMenu, setShowMenu] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  
  const audioCtxRef = useRef(null);
  const menuRef = useRef(null);
  const soundIntervalRef = useRef(null);
  const soundLoopActive = useRef(false);
  
  // Refs to access latest values in intervals/callbacks
  const pendingCountRef = useRef(0);
  const enabledRef = useRef(enabled);
  const selectedSoundRef = useRef(selectedSound);

  // Keep refs in sync
  useEffect(() => { pendingCountRef.current = pendingCount; }, [pendingCount]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { selectedSoundRef.current = selectedSound; }, [selectedSound]);

  // Get or create AudioContext
  const getAudioCtx = () => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  };

  // Play sound function with cooldown protection (persisted in localStorage)
  const playSound = (soundId) => {
    const now = Date.now();
    const lastTime = parseInt(localStorage.getItem('lastSoundTime') || '0', 10);
    
    // Don't play if less than 25 seconds since last sound
    if (now - lastTime < 25000) {
      return;
    }
    localStorage.setItem('lastSoundTime', now.toString());
    
    try {
      const ctx = getAudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => createSound(soundId || selectedSoundRef.current, ctx)).catch(() => {});
      } else {
        createSound(soundId || selectedSoundRef.current, ctx);
      }
    } catch (e) {
      console.log('Error playing sound:', e);
    }
  };
  
  // Play sound for menu preview (bypasses cooldown)
  const playSoundPreview = (soundId) => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => createSound(soundId, ctx)).catch(() => {});
      } else {
        createSound(soundId, ctx);
      }
    } catch (e) {
      console.log('Error playing sound:', e);
    }
  };

  // Load notification status from server (sync across devices)
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const authToken = localStorage.getItem('token');
        if (!authToken) {
          setStatusLoaded(true);
          return;
        }
        
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/admin/notification-status`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setEnabled(data.enabled);
          enabledRef.current = data.enabled;
        }
      } catch (e) {
        console.log('Error loading notification status:', e);
      } finally {
        setStatusLoaded(true);
      }
    };
    
    loadStatus();
  }, []);

  // Initialize Firebase Push Notifications (web) + native Android notifications
  useEffect(() => {
    const initPush = async () => {
      try {
        const token = await requestNotificationPermission();
        if (token) {
          console.log('Push notifications enabled');
          setPushEnabled(true);
          
          // Register token with backend
          const authToken = localStorage.getItem('token');
          if (authToken) {
            const API = process.env.REACT_APP_BACKEND_URL;
            await fetch(`${API}/api/admin/register-push-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({ token })
            });
          }
        }
      } catch (error) {
        console.log('Push notification init error:', error);
      }
    };

    if (enabled) {
      initPush();
      // Native Android shell: ask for POST_NOTIFICATIONS on Android 13+ if not
      // yet granted. No-op on <13 (permission not required) and on web (bridge
      // not present). Never throws.
      if (isNativeNotifierAvailable() && !hasNativeNotificationPermission()) {
        requestNativeNotificationPermission();
      }
    }

    // ── Native FCM token registration ──
    // Inside the Android shell, register the DEVICE's Firebase Cloud Messaging
    // token with the backend so pushes reach the phone natively — even when
    // the app is closed. Poll briefly because Firebase resolves the token
    // asynchronously after cold start.
    let fcmPollTimer = null;
    let fcmPollAttempts = 0;
    let lastRegisteredToken = '';
    const registerNativeToken = async () => {
      if (!enabled) return;
      if (!isNativeNotifierAvailable()) return;
      const token = getNativeFcmToken();
      if (!token || token === lastRegisteredToken) return;
      const authToken = localStorage.getItem('token');
      if (!authToken) return;
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/admin/register-push-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({ token }),
        });
        if (res.ok) {
          lastRegisteredToken = token;
          setPushEnabled(true);
          console.log('Native FCM token registered');
        }
      } catch (e) {
        console.log('Native FCM register failed:', e?.message || e);
      }
    };
    if (isNativeNotifierAvailable() && enabled) {
      refreshNativeFcmToken();
      // Poll up to 10 times over ~15s until Firebase resolves the token.
      fcmPollTimer = setInterval(() => {
        fcmPollAttempts += 1;
        registerNativeToken();
        if (lastRegisteredToken || fcmPollAttempts > 10) {
          clearInterval(fcmPollTimer);
          fcmPollTimer = null;
        }
      }, 1500);
    }

    // Listen for foreground messages (don't play sound - local loop handles it)
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Foreground push received:', payload);
      // Don't play sound here - the local 30s loop already handles it
      // This just logs that the push arrived while app is open
    });

    return () => {
      if (unsubscribe) unsubscribe();
      if (fcmPollTimer) clearInterval(fcmPollTimer);
    };
  }, [enabled]);

  // Unlock audio on user interaction
  useEffect(() => {
    const unlock = () => {
      const ctx = getAudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          if (pendingCountRef.current > 0 && enabledRef.current) {
            createSound(selectedSoundRef.current, ctx);
          }
        }).catch(() => {});
      }
    };
    
    document.addEventListener('click', unlock, { passive: true });
    document.addEventListener('touchstart', unlock, { passive: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  // Check pending balance requests - runs every 60 seconds
  useEffect(() => {
    const checkPending = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const API = process.env.REACT_APP_BACKEND_URL;
        // Honor the admin's toggle in the Balance Requests panel: when
        // "Ocultar solicitudes de clientes de revendedores" is ON (default),
        // the badge counts only direct-client requests so it matches what's
        // visible on screen.
        let hideResellerClients = true;
        try {
          const v = localStorage.getItem('admin_hide_reseller_balance_requests');
          hideResellerClients = v === null ? true : v === 'true';
        } catch (_e) {}
        const url = `${API}/api/admin/pending-balance-count` +
          (hideResellerClients ? '' : '?include_reseller_clients=true');
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          const newCount = data.count || 0;
          setPendingCount(newCount);
        }
      } catch (e) {
        console.log('Error checking pending:', e);
      }
    };

    checkPending();
    // Poll every 15s — matches the pre-native cadence the admins were used to
    // (fast enough that new pending requests feel real-time; sound cooldown is
    // 25s so this doesn't create audio spam).
    const interval = setInterval(checkPending, 15000);
    return () => clearInterval(interval);
  }, []);

  // Sound loop - plays every 30 seconds when there are pending requests
  useEffect(() => {
    // Don't start until status is loaded from server
    if (!statusLoaded) return;
    
    const shouldPlay = pendingCount > 0 && enabled;
    
    // If conditions changed to stop playing
    if (!shouldPlay) {
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
      }
      soundLoopActive.current = false;
      return;
    }
    
    // If loop is already active, don't restart it
    if (soundLoopActive.current) {
      return;
    }
    
    // Start new loop
    soundLoopActive.current = true;
    playSound(selectedSound);
    
    soundIntervalRef.current = setInterval(() => {
      if (pendingCountRef.current > 0 && enabledRef.current) {
        playSound(selectedSoundRef.current);
      }
    }, 30000);

    return () => {
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
      }
      soundLoopActive.current = false;
    };
  }, [pendingCount > 0, enabled, statusLoaded]);

  // Save sound preference to localStorage (only sound, not enabled status)
  useEffect(() => {
    localStorage.setItem('notif_sound', selectedSound);
  }, [selectedSound]);

  // ── Native Android system notification ──
  // Mirror the pending badge into a real OS notification when running inside
  // the RecargameYa Android shell. Uses a fixed tag+id so subsequent updates
  // replace the same notification instead of stacking. Cancelled automatically
  // when there are no pending requests.
  useEffect(() => {
    if (!statusLoaded) return;
    if (!isNativeNotifierAvailable()) return;
    if (!enabled) {
      nativeCancel('pending-balance', 1001);
      return;
    }
    if (pendingCount > 0) {
      nativeNotify({
        title: `${pendingCount} solicitud${pendingCount !== 1 ? 'es' : ''} pendiente${pendingCount !== 1 ? 's' : ''}`,
        body: 'Toca para revisar y aprobar en el panel de administración.',
        tag: 'pending-balance',
        id: 1001,
      });
    } else {
      nativeCancel('pending-balance', 1001);
    }
  }, [pendingCount, enabled, statusLoaded]);

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleEnabled = async () => {
    const newVal = !enabled;
    setEnabled(newVal);
    
    const authToken = localStorage.getItem('token');
    const API = process.env.REACT_APP_BACKEND_URL;
    
    if (newVal) {
      playSoundPreview(selectedSound);
      // Re-initialize push when enabling
      const token = await requestNotificationPermission();
      if (token) {
        setPushEnabled(true);
        if (authToken) {
          await fetch(`${API}/api/admin/register-push-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({ token })
          });
        }
      }
    }
    
    // Toggle push notifications on backend
    if (authToken) {
      try {
        await fetch(`${API}/api/admin/toggle-push-notifications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ enabled: newVal })
        });
      } catch (e) {
        console.log('Error toggling push notifications:', e);
      }
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <div className="flex items-center gap-2">
        {pendingCount > 0 && (
          <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
            {pendingCount} solicitud{pendingCount !== 1 ? 'es' : ''}
          </span>
        )}
        <button
          onClick={toggleEnabled}
          className={`p-2 rounded-lg transition-colors ${
            enabled 
              ? 'text-green-400 hover:bg-green-400/10' 
              : 'text-slate-400 hover:bg-slate-400/10'
          }`}
          title={enabled ? `Notificaciones activadas${pushEnabled ? ' (Push ON)' : ''}` : 'Notificaciones desactivadas'}
        >
          {enabled ? <Bell size={20} /> : <BellOff size={20} />}
        </button>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 rounded-lg text-slate-400 hover:bg-slate-400/10 flex items-center gap-1"
          title="Seleccionar sonido"
        >
          <Volume2 size={18} />
          <ChevronDown size={14} />
        </button>
      </div>
      {showMenu && (
        <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 min-w-[180px]">
          <div className="p-2 text-xs text-slate-400 border-b border-slate-700">
            Sonido de notificación
            {pushEnabled && <span className="ml-1 text-green-400">(Push ON)</span>}
          </div>
          {SOUNDS.map((sound) => (
            <button
              key={sound.id}
              onClick={() => {
                setSelectedSound(sound.id);
                playSoundPreview(sound.id);
                setShowMenu(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 flex items-center justify-between ${
                selectedSound === sound.id ? 'text-blue-400' : 'text-slate-300'
              }`}
            >
              {sound.label}
              {selectedSound === sound.id && <span className="text-blue-400">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
